// Section 24.9: invariants. `assertInvariants` runs after every phase in dev and test builds;
// a violation is a bug, never a game outcome. Each violation has a code and names the id.
import type { Config } from '../config';
import { SimError } from './errors';
import type { Registry } from './registry';
import { RATION_RESOURCES, RESOURCES } from './constants';
import { sortedIds } from './sorted';
import type { BuildingTypeId, GameState, PhaseId } from './types';

export type InvariantCode =
  | 'household-link'
  | 'family-link'
  | 'job-unknown'
  | 'job-child'
  | 'range'
  | 'skill-range'
  | 'age'
  | 'stock-range'
  | 'ration-overdrawn'
  | 'build-unknown'
  | 'building-limit'
  | 'building-condition'
  | 'decision-log'
  | 'rng-stuck'
  | 'prompt-cursor'
  | 'bad-value'
  | 'id-key'
  | 'illness-state'
  | 'justice-state'
  // Sections 14 and 19 (stream E2).
  | 'arrival-state'
  | 'event-state';

export interface InvariantViolation {
  code: InvariantCode;
  message: string;
  ids: string[];
}

export interface InvariantOptions {
  cfg: Config;
  /** Enables building-limit checks (limits live on content defs). */
  registry?: Registry;
  /** The state before the season, to check aging and rng movement across a completed season. */
  previous?: GameState;
  phase?: PhaseId | 'step' | 'new-game';
}

const EPS = 1e-6;

function checkValues(
  value: unknown,
  previous: unknown,
  path: string,
  out: InvariantViolation[],
): void {
  if (value === previous) return; // structural sharing: unchanged subtree already checked
  if (typeof value === 'number') {
    if (!Number.isFinite(value))
      out.push({ code: 'bad-value', message: `${path} is ${value}`, ids: [path] });
    return;
  }
  if (
    value === undefined ||
    typeof value === 'function' ||
    typeof value === 'symbol' ||
    typeof value === 'bigint'
  ) {
    out.push({ code: 'bad-value', message: `${path} is ${typeof value}`, ids: [path] });
    return;
  }
  if (value === null || typeof value !== 'object') return;
  const prev =
    previous !== null && typeof previous === 'object'
      ? (previous as Record<string, unknown>)
      : undefined;
  if (Array.isArray(value)) {
    const prevArr = Array.isArray(previous) ? (previous as unknown[]) : undefined;
    value.forEach((v: unknown, i) => {
      checkValues(v, prevArr?.[i], `${path}[${i}]`, out);
    });
    return;
  }
  const proto: unknown = Object.getPrototypeOf(value);
  if (proto !== Object.prototype && proto !== null) {
    out.push({ code: 'bad-value', message: `${path} is not plain data`, ids: [path] });
    return;
  }
  for (const key of Object.keys(value)) {
    checkValues((value as Record<string, unknown>)[key], prev?.[key], `${path}.${key}`, out);
  }
}

function inRange(x: number, min: number, max: number): boolean {
  return x >= min - EPS && x <= max + EPS;
}

function checkPeople(state: GameState, cfg: Config, out: InvariantViolation[]): void {
  const childWorkSeasons = cfg.work.childWorkFromYears * cfg.people.seasonsPerYear;
  const minWorkSeasons =
    state.policies.childLabour === 'never' ? cfg.people.adultAtSeasons : childWorkSeasons;
  for (const id of sortedIds(state.people)) {
    const p = state.people[id];
    if (p === undefined) continue;
    if (p.id !== id)
      out.push({ code: 'id-key', message: `person key ${id} holds ${p.id}`, ids: [id] });
    const h = state.households[p.householdId];
    if (h === undefined) {
      out.push({
        code: 'household-link',
        message: `${id} points at missing household ${p.householdId}`,
        ids: [id],
      });
    } else if (!h.memberIds.includes(id)) {
      out.push({
        code: 'household-link',
        message: `${p.householdId} does not list ${id}`,
        ids: [id, h.id],
      });
    }
    if (
      p.partnerId !== null &&
      state.people[p.partnerId] === undefined &&
      state.formerPeople[p.partnerId] === undefined
    ) {
      out.push({ code: 'family-link', message: `${id} partner ${p.partnerId} unknown`, ids: [id] });
    }
    // Section 15 (Stream D): partnership is symmetric between living people; no self-links.
    const partner = p.partnerId === null ? undefined : state.people[p.partnerId];
    if (p.partnerId === id || (partner !== undefined && partner.partnerId !== id)) {
      out.push({
        code: 'family-link',
        message: `${id} partner ${String(p.partnerId)} does not point back`,
        ids: [id],
      });
    }
    // Section 11 (Stream D): a master is someone else, living or former.
    if (
      p.masterId !== null &&
      (p.masterId === id ||
        (state.people[p.masterId] === undefined && state.formerPeople[p.masterId] === undefined))
    ) {
      out.push({ code: 'family-link', message: `${id} master ${p.masterId} unknown`, ids: [id] });
    }
    if (!(p.job in cfg.jobs))
      out.push({ code: 'job-unknown', message: `${id} has job ${p.job}`, ids: [id] });
    if (p.job !== 'rest' && p.ageSeasons < minWorkSeasons) {
      out.push({
        code: 'job-child',
        message: `${id} aged ${p.ageSeasons} seasons works as ${p.job}`,
        ids: [id],
      });
    }
    for (const field of ['health', 'strength', 'happiness', 'trust'] as const) {
      if (!inRange(p[field], 0, cfg.people.statMax)) {
        out.push({ code: 'range', message: `${id}.${field} = ${p[field]}`, ids: [id] });
      }
    }
    for (const [skill, level] of Object.entries(p.skills)) {
      if (!(skill in cfg.jobs) || !inRange(level, 0, cfg.skills.max)) {
        out.push({ code: 'skill-range', message: `${id}.skills.${skill} = ${level}`, ids: [id] });
      }
    }
    if (p.ageSeasons < 0) out.push({ code: 'age', message: `${id} has negative age`, ids: [id] });
    // Section 12 and 19 (stream M3b): only sick people are quarantined; timers are positive.
    if (p.status.quarantined && p.status.sick === null) {
      out.push({ code: 'illness-state', message: `${id} quarantined but not sick`, ids: [id] });
    }
    if (
      (p.status.sick !== null && p.status.sick.seasonsRemaining < 0) ||
      (p.status.injured !== null && p.status.injured.seasonsRemaining < 0)
    ) {
      out.push({ code: 'illness-state', message: `${id} has a negative illness timer`, ids: [id] });
    }
  }
  for (const hid of sortedIds(state.households)) {
    const h = state.households[hid];
    if (h === undefined) continue;
    if (h.id !== hid)
      out.push({ code: 'id-key', message: `household key ${hid} holds ${h.id}`, ids: [hid] });
    for (const pid of h.memberIds) {
      const member = state.people[pid];
      if (member?.householdId !== hid) {
        out.push({
          code: 'household-link',
          message: `${hid} lists ${pid} who is not a member`,
          ids: [hid, pid],
        });
      }
    }
    if (h.buildingId !== null && state.buildings[h.buildingId] === undefined) {
      out.push({
        code: 'household-link',
        message: `${hid} sleeps in missing ${h.buildingId}`,
        ids: [hid],
      });
    }
  }
}

function checkStocksAndBuildings(
  state: GameState,
  opts: InvariantOptions,
  out: InvariantViolation[],
): void {
  const { cfg } = opts;
  for (const r of RESOURCES) {
    const s = state.stocks[r];
    if (s.amount < -EPS || s.amount > s.capacity * cfg.resources.hardCapFactor + EPS) {
      out.push({
        code: 'stock-range',
        message: `${r} = ${s.amount} (capacity ${s.capacity})`,
        ids: [r],
      });
    }
  }
  if (state.seedReserved < -EPS)
    out.push({ code: 'stock-range', message: 'seedReserved < 0', ids: ['seed'] });
  if (!inRange(state.toolDurability, 0, 1))
    out.push({ code: 'range', message: 'toolDurability out of 0..1', ids: [] });
  const report = state.currentReport;
  if (report !== null) {
    for (const r of RATION_RESOURCES) {
      const d = report.distribution[r];
      if (d !== undefined && d.distributed > d.available + EPS) {
        out.push({
          code: 'ration-overdrawn',
          message: `${r}: ${d.distributed} > ${d.available}`,
          ids: [r],
        });
      }
      // Section 17 (stream A): allotted shares are fractions of need.
      for (const [id, share] of Object.entries(report.rations?.[r] ?? {})) {
        if (!inRange(share, 0, 1))
          out.push({ code: 'range', message: `${r} ration ${id} = ${share}`, ids: [id] });
      }
    }
  }
  for (const order of state.buildQueue) {
    if (!(order.buildingType in cfg.buildings)) {
      out.push({
        code: 'build-unknown',
        message: `order ${order.id} builds ${order.buildingType}`,
        ids: [order.id],
      });
    }
  }
  const counts = new Map<BuildingTypeId, number>();
  for (const id of sortedIds(state.buildings)) {
    const b = state.buildings[id];
    if (b === undefined) continue;
    if (b.id !== id)
      out.push({ code: 'id-key', message: `building key ${id} holds ${b.id}`, ids: [id] });
    if (!(b.type in cfg.buildings))
      out.push({ code: 'build-unknown', message: `${id} is ${b.type}`, ids: [id] });
    if (!inRange(b.condition, 0, 100)) {
      out.push({
        code: 'building-condition',
        message: `${id} condition ${b.condition}`,
        ids: [id],
      });
    }
    counts.set(b.type, (counts.get(b.type) ?? 0) + 1);
  }
  if (opts.registry !== undefined) {
    const pop = Object.keys(state.people).length;
    for (const [type, n] of counts) {
      const limit = opts.registry.buildings[type].limit;
      // Per-people limits are checked when queuing; a shrinking population may leave a surplus.
      if (limit.kind === 'fixed' && n > limit.max) {
        out.push({
          code: 'building-limit',
          message: `${n} ${type} exceeds limit ${limit.max} (pop ${pop})`,
          ids: [type],
        });
      }
    }
  }
}

function checkSeason(state: GameState, opts: InvariantOptions, out: InvariantViolation[]): void {
  if (state.decisionLog.length !== state.turn) {
    out.push({
      code: 'decision-log',
      message: `decisionLog ${state.decisionLog.length} != turn ${state.turn}`,
      ids: [],
    });
  }
  if (state.pendingPrompts.length > 0 && state.phaseCursor === null) {
    out.push({ code: 'prompt-cursor', message: 'pendingPrompts set without phaseCursor', ids: [] });
  }
  const prev = opts.previous;
  if (
    prev !== undefined &&
    state.phaseCursor === null &&
    prev.phaseCursor === null &&
    state.turn === prev.turn + 1
  ) {
    if (Object.keys(prev.people).length > 0 && state.rngState === prev.rngState) {
      out.push({ code: 'rng-stuck', message: 'rngState did not advance over a season', ids: [] });
    }
    for (const id of sortedIds(state.people)) {
      const before = prev.people[id];
      const after = state.people[id];
      if (
        before !== undefined &&
        after !== undefined &&
        after.ageSeasons !== before.ageSeasons + 1
      ) {
        out.push({
          code: 'age',
          message: `${id} aged ${before.ageSeasons} -> ${after.ageSeasons}`,
          ids: [id],
        });
      }
    }
  }
}

/** Section 13 (stream M4a): cases, custody and justice prompts point at each other consistently. */
function checkJustice(state: GameState, out: InvariantViolation[]): void {
  for (const id of sortedIds(state.cases)) {
    const c = state.cases[id];
    if (c === undefined) continue;
    if (c.id !== id)
      out.push({ code: 'id-key', message: `case key ${id} holds ${c.id}`, ids: [id] });
    if (c.status === 'pending' && c.response !== null)
      out.push({ code: 'justice-state', message: `${id} is pending with a response`, ids: [id] });
    if (state.people[c.offenderId] === undefined && state.formerPeople[c.offenderId] === undefined)
      out.push({ code: 'justice-state', message: `${id} offender unknown`, ids: [id] });
  }
  for (const pid of sortedIds(state.people)) {
    const custody = state.people[pid]?.status.inCustody;
    if (custody === undefined || custody === null) continue;
    if (state.cases[custody.caseId] === undefined || custody.seasonsRemaining <= 0)
      out.push({ code: 'justice-state', message: `${pid} held without a case`, ids: [pid] });
  }
  for (const prompt of state.openPrompts) {
    if (prompt.kind !== 'justice') continue;
    if (state.cases[prompt.caseId]?.status !== 'pending')
      out.push({
        code: 'justice-state',
        message: `${prompt.id} has no pending case`,
        ids: [prompt.id],
      });
  }
  for (const e of state.deterrence) {
    if (!(e.multiplier > 0))
      out.push({
        code: 'justice-state',
        message: `deterrence ${e.source} is ${e.multiplier}`,
        ids: [],
      });
  }
}

export function checkInvariants(state: GameState, opts: InvariantOptions): InvariantViolation[] {
  const out: InvariantViolation[] = [];
  checkValues(state, opts.previous, 'state', out);
  checkPeople(state, opts.cfg, out);
  checkStocksAndBuildings(state, opts, out);
  checkSeason(state, opts, out);
  checkJustice(state, out);
  checkArrivalsAndEvents(state, out);
  return out;
}

/**
 * Sections 14 and 19 (stream E2): a group still at the gate has none of its candidates in
 * `people`; an open arrival prompt names a group waiting on it; guests belong to a group of
 * guests; deck draws are for this season or later; an effect ends no earlier than it starts.
 */
function checkArrivalsAndEvents(state: GameState, out: InvariantViolation[]): void {
  const guestGroups = new Set<string>();
  for (const id of sortedIds(state.arrivalGroups)) {
    const g = state.arrivalGroups[id];
    if (g === undefined) continue;
    if (g.status === 'pending') {
      for (const c of g.candidates)
        if (state.people[c.id] !== undefined)
          out.push({
            code: 'arrival-state',
            message: `${id} is at the gate but ${c.id} is already a person`,
            ids: [id, c.id],
          });
    }
    if (g.status === 'guests' && g.settled !== true)
      for (const pid of g.admittedIds ?? []) guestGroups.add(pid);
  }
  for (const p of state.openPrompts) {
    if (p.kind !== 'event' || p.groupId === null) continue;
    if (state.arrivalGroups[p.groupId]?.promptId !== p.id)
      out.push({
        code: 'arrival-state',
        message: `arrival prompt ${p.id} has no group waiting on it`,
        ids: [p.id],
      });
  }
  for (const id of sortedIds(state.people)) {
    const p = state.people[id];
    if ((p?.status.guest ?? null) !== null && !guestGroups.has(id))
      out.push({ code: 'arrival-state', message: `${id} is a guest of no group`, ids: [id] });
  }
  const floor = state.currentReport === null ? state.turn + 1 : state.turn;
  for (const d of (state.eventDraws as GameState['eventDraws'] | undefined) ?? [])
    if (d.turn < floor - 1)
      out.push({ code: 'event-state', message: `stale deck draw for turn ${d.turn}`, ids: [] });
  for (const e of state.activeEffects)
    if (e.untilTurn < e.sinceTurn)
      out.push({
        code: 'event-state',
        message: `effect ${e.id} ends before it starts`,
        ids: [e.id],
      });
}

export function assertInvariants(state: GameState, opts: InvariantOptions): void {
  const violations = checkInvariants(state, opts);
  const first = violations[0];
  if (first === undefined) return;
  const detail = violations
    .slice(0, 5)
    .map((v) => `${v.code}: ${v.message}`)
    .join('; ');
  throw new SimError(
    'invariant',
    `${violations.length} invariant violation(s) after ${opts.phase ?? 'check'}: ${detail}`,
    {
      turn: state.turn,
      phase: opts.phase ?? 'check',
      ids: first.ids,
      code: first.code,
    },
  );
}
